import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../Layout';
import { showToast } from '../../helpers/customToast';
import { verifyPaymentStatus } from '../../helpers/stripe';
import { getOrderByDocumentId } from '../../helpers/order';
import { createSwiklyDeposit } from '../../helpers/swikly';
import { CartManager } from '../../helpers/cart';

const PaymentCompletePage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const verificationAttempted = useRef(false);

    useEffect(() => {
        const verifyPayment = async () => {
            // Skip if verification was already attempted
            if (verificationAttempted.current) {
                return;
            }
            verificationAttempted.current = true;

            try {
                const documentId = searchParams.get('document_id');
                const sessionId = searchParams.get('session_id');

                if (!documentId || !sessionId) {
                    throw new Error('Missing required parameters');
                }

                // console.log('Verifying payment for order:', documentId, 'session:', sessionId);

                // Get order details by documentId first
                const order = await getOrderByDocumentId(documentId);
                if (!order) {
                    throw new Error('Order not found');
                }

                // console.log('Found order:', order);

                // Verify payment status
                const paymentStatus = await verifyPaymentStatus(sessionId, documentId);
                
                // console.log('Payment status:', paymentStatus);
                
                if (paymentStatus.success) {
                    let swiklyResponse;
                    
                    // Only create Swikly deposit if it doesn't exist
                    if (!order.swikly_request_id) {
                        const firstOrderItem = order.order_items[0];
                        swiklyResponse = await createSwiklyDeposit({
                            orderId: order.id,
                            depositAmount: order.deposit,
                            customerEmail: order.user.email,
                            firstName: order.user.first_name,
                            lastName: order.user.last_name,
                            startDate: firstOrderItem.start_date,
                            endDate: firstOrderItem.end_date
                        });
                    }
                    // console.log('Swikly response: ', swiklyResponse);
                    
                    // Redirect to order confirmation
                    navigate('/order-confirmation', {
                        state: { 
                            orderId: order.id,
                            documentId: documentId,
                            paymentConfirmed: true,
                            swiklyLink: swiklyResponse?.link || order.swikly_url || null
                        }
                    });
                } else {
                    throw new Error('Payment verification failed');
                }
            } catch (error) {
                console.error('Payment verification error:', error);
                showToast('Une erreur est survenue lors de la vérification de votre paiement. Veuillez contacter le support.', 'error');
                // navigate('/');
            } finally {
                setLoading(false);
                await CartManager.clearCart();
            }
        };

        verifyPayment();
    }, [searchParams, navigate]);

    if (loading) {
        return (
            <Layout>
                <div className="container my-5">
                    <div className="text-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Verifying payment...</span>
                        </div>
                        <p className="mt-3">Verifying your payment...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return null;
};

export default PaymentCompletePage;